import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'];
const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
// Custom elements built on PaginatedList (infinite scroll)
const PAGINATED_LISTS = 'paginated-list, results-list, blog-posts-list';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.addEventListener('click', this.#onStoreLocationClick);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.removeEventListener('click', this.#onStoreLocationClick);
    this.#resumeInfiniteScroll();
  }

  /**
   * The store section lives in the footer group. On pages with infinite scroll (collections,
   * search) the smooth scroll towards it crosses the "view more" sentinel, which loads the next
   * page and pushes the footer away. Pause infinite loading while the browser scrolls to the
   * anchor, and resume it on the next scroll the user starts themselves.
   * @param {MouseEvent} event
   */
  #onStoreLocationClick = (event) => {
    const link = /** @type {HTMLElement} */ (event.target).closest('a.header-actions__store-location');
    if (!(link instanceof HTMLAnchorElement) || !document.getElementById(link.hash.slice(1))) return;

    const lists = /** @type {NodeListOf<HTMLElement & { infinityScrollObserver?: IntersectionObserver }>} */ (
      document.querySelectorAll(PAGINATED_LISTS)
    );
    let paused = false;
    for (const list of lists) {
      if (!list.infinityScrollObserver) continue;
      list.infinityScrollObserver.disconnect();
      paused = true;
    }
    if (!paused) return;

    for (const type of USER_SCROLL_EVENTS) {
      window.addEventListener(type, this.#onUserScrollIntent, { passive: true, capture: true });
    }
  };

  /** @param {Event} event */
  #onUserScrollIntent = (event) => {
    if (event instanceof KeyboardEvent && !SCROLL_KEYS.includes(event.key)) return;
    this.#resumeInfiniteScroll();
  };

  #resumeInfiniteScroll() {
    for (const type of USER_SCROLL_EVENTS) {
      window.removeEventListener(type, this.#onUserScrollIntent, { capture: true });
    }

    const lists = /** @type {NodeListOf<HTMLElement & { infinityScrollObserver?: IntersectionObserver, refs?: Record<string, Element | undefined> }>} */ (
      document.querySelectorAll(PAGINATED_LISTS)
    );
    for (const list of lists) {
      const observer = list.infinityScrollObserver;
      if (!observer) continue;
      for (const sentinel of [list.refs?.viewMorePrevious, list.refs?.viewMoreNext]) {
        if (sentinel) observer.observe(sentinel);
      }
    }
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
